// Firebase Storage functions for file uploads and management
import { 
  ref, 
  uploadBytes, 
  uploadBytesResumable,
  getDownloadURL, 
  deleteObject 
} from "firebase/storage";
import { storage } from "./firebase-config";

// Fixed Video upload function with better error handling
export const uploadVideo = async (userId, videoBlob) => {
  try {
    // Create a unique filename using userId and timestamp
    const timestamp = Date.now();
    const filename = `user_videos/${userId}_${timestamp}.webm`;
    
    console.log(`Uploading video as ${filename}, size: ${(videoBlob.size / (1024 * 1024)).toFixed(2)} MB`);
    
    // Create a reference to the file location in Firebase Storage
    const videoRef = ref(storage, filename);
    
    // Upload the video blob with progress tracking
    const uploadTask = uploadBytesResumable(videoRef, videoBlob);
    
    // Return a promise that resolves when upload completes
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Report progress
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`Upload progress: ${progress.toFixed(1)}%`);
        },
        (error) => {
          // Handle unsuccessful uploads
          console.error("Upload failed:", error);
          
          let errorMessage = "Failed to upload video.";
          switch (error.code) {
            case 'storage/unauthorized':
              errorMessage = "You don't have permission to upload files.";
              break;
            case 'storage/canceled':
              errorMessage = "Upload was canceled.";
              break;
            case 'storage/quota-exceeded':
              errorMessage = "Storage quota exceeded.";
              break;
            case 'storage/invalid-argument':
              errorMessage = "Invalid file format or corrupted video.";
              break;
            case 'storage/unknown':
            default:
              errorMessage = `Unknown error occurred during upload: ${error.message}`;
              break;
          }
          
          reject({ success: false, error: errorMessage });
        },
        async () => {
          // Handle successful uploads
          try {
            console.log('Video uploaded successfully');
            // Get the download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log('Video download URL:', downloadURL);
            
            resolve({
              success: true,
              videoUrl: downloadURL,
              storagePath: filename,
              timestamp: timestamp
            });
          } catch (urlError) {
            console.error("Error getting download URL:", urlError);
            reject({ 
              success: false, 
              error: "Video was uploaded but couldn't retrieve download URL"
            });
          }
        }
      );
    });
  } catch (error) {
    console.error("Error setting up video upload:", error);
    return { success: false, error: error.message };
  }
};

// Delete existing video
export const deleteVideo = async (storagePath) => {
  try {
    if (!storagePath) {
      console.warn("No storage path provided for video deletion");
      return { success: true }; // Nothing to delete
    }
    
    console.log(`Deleting video at path: ${storagePath}`);
    const videoRef = ref(storage, storagePath);
    await deleteObject(videoRef);
    console.log('Video deleted successfully');
    
    return { success: true };
  } catch (error) {
    console.error("Error deleting video:", error);
    return { success: false, error: error.message };
  }
};

// Upload resume to Firebase Storage
export const uploadResumeToStorage = async (userId, file, progressCallback = null) => {
  try {
    if (!userId || !file) {
      return { success: false, error: "Missing required information" };
    }
    
    // Validate file type
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      return { success: false, error: 'Please upload a valid resume file (PDF or Word document)' };
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      return { success: false, error: 'File size must be less than 5MB' };
    }
    
    // Create filename in the format expected by processor.js
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const timestamp = Date.now();
    const filename = `user_resumes/${userId}_${timestamp}.${fileExtension}`;
    
    console.log(`Uploading resume as ${filename}`);
    
    // Create a reference to Firebase Storage
    const resumeRef = ref(storage, filename);
    
    // Use uploadBytesResumable for progress tracking
    const uploadTask = uploadBytesResumable(resumeRef, file);
    
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Calculate and report progress
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`Resume upload progress: ${progress.toFixed(1)}%`);
          
          if (progressCallback) {
            progressCallback(Math.round(progress));
          }
        },
        (error) => {
          // Handle upload errors
          console.error('Resume upload failed:', error);
          
          let errorMessage = "Failed to upload resume.";
          switch (error.code) {
            case 'storage/unauthorized':
              errorMessage = "You don't have permission to upload files.";
              break;
            case 'storage/canceled':
              errorMessage = "Upload was canceled.";
              break;
            case 'storage/quota-exceeded':
              errorMessage = "Storage quota exceeded.";
              break;
            case 'storage/invalid-argument':
              errorMessage = "Invalid file format.";
              break;
            default:
              errorMessage = `Upload error: ${error.message}`;
              break;
          }
          
          reject({ success: false, error: errorMessage });
        },
        async () => {
          // Upload completed successfully
          try {
            // Get download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log('Resume download URL:', downloadURL);
            
            resolve({
              success: true,
              resumeUrl: downloadURL,
              resumeName: file.name,
              storagePath: filename,
              timestamp: timestamp
            });
          } catch (urlError) {
            console.error("Error getting resume download URL:", urlError);
            reject({ 
              success: false, 
              error: "Resume was uploaded but couldn't retrieve download URL"
            });
          }
        }
      );
    });
  } catch (error) {
    console.error("Error setting up resume upload:", error);
    return { success: false, error: error.message };
  }
};

// Save reflection video to Firebase Storage
export const saveReflectionVideoToStorage = async (userId, videoBlob, questionIndex, questionText) => {
  try {
    // Create a unique filename
    const timestamp = Date.now();
    const filename = `user_reflections/${userId}/question_${questionIndex}_${timestamp}.webm`;
    
    console.log(`Uploading reflection video as ${filename}`);
    
    const videoRef = ref(storage, filename);
    
    // Upload the video blob
    const uploadResult = await uploadBytes(videoRef, videoBlob);
    console.log("Upload completed:", uploadResult);
    
    // Get the URL
    const downloadURL = await getDownloadURL(videoRef);
    console.log("Download URL:", downloadURL);
    
    return {
      success: true,
      videoUrl: downloadURL,
      storagePath: filename,
      timestamp: timestamp
    };
  } catch (error) {
    console.error("Error saving reflection video:", error);
    return { success: false, error: error.message };
  }
};

// Delete a reflection video from storage
export const deleteReflectionVideoFromStorage = async (storagePath) => {
  try {
    if (storagePath) {
      const videoRef = ref(storage, storagePath);
      await deleteObject(videoRef);
    }
    
    return { success: true };
  } catch (error) {
    console.error("Error deleting reflection video:", error);
    return { success: false, error: error.message };
  }
};

// Upload forum attachments to Firebase Storage
export const uploadForumAttachments = async (userId, files, replyId) => {
  try {
    if (!userId || !files || files.length === 0) {
      return { success: true, attachments: [] };
    }

    console.log(`Uploading ${files.length} forum attachments for user ${userId}`);
    
    const uploadPromises = files.map(async (file, index) => {
      try {
        // Create unique filename
        const timestamp = Date.now();
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const filename = `forum_attachments/${userId}/${replyId}_${timestamp}_${index}_${sanitizedName}`;
        
        // Create storage reference
        const fileRef = ref(storage, filename);
        
        // Upload file
        const uploadResult = await uploadBytes(fileRef, file);
        
        // Get download URL
        const downloadURL = await getDownloadURL(fileRef);
        
        return {
          success: true,
          originalName: file.name,
          fileName: sanitizedName,
          downloadURL,
          storagePath: filename,
          size: file.size,
          type: file.type,
          uploadedAt: new Date().toISOString()
        };
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        return {
          success: false,
          originalName: file.name,
          error: error.message
        };
      }
    });

    const results = await Promise.all(uploadPromises);
    const successful = results.filter(result => result.success);
    const failed = results.filter(result => !result.success);

    if (failed.length > 0) {
      console.warn(`${failed.length} files failed to upload:`, failed);
    }

    return {
      success: true,
      attachments: successful,
      failedUploads: failed
    };
  } catch (error) {
    console.error('Error uploading forum attachments:', error);
    return {
      success: false,
      error: error.message,
      attachments: []
    };
  }
};

// Delete forum attachments from storage
export const deleteForumAttachments = async (attachments) => {
  try {
    if (!attachments || attachments.length === 0) {
      return { success: true };
    }

    const deletePromises = attachments.map(async (attachment) => {
      try {
        if (attachment.storagePath) {
          const fileRef = ref(storage, attachment.storagePath);
          await deleteObject(fileRef);
          return { success: true, path: attachment.storagePath };
        }
        return { success: true, path: 'no-path' };
      } catch (error) {
        console.error(`Error deleting attachment ${attachment.storagePath}:`, error);
        return { success: false, path: attachment.storagePath, error: error.message };
      }
    });

    const results = await Promise.all(deletePromises);
    const failed = results.filter(result => !result.success);

    if (failed.length > 0) {
      console.warn(`${failed.length} attachments failed to delete:`, failed);
    }

    return { success: true, deletionResults: results };
  } catch (error) {
    console.error('Error deleting forum attachments:', error);
    return { success: false, error: error.message };
  }
};

// Upload resume to Firebase Storage and update user submission (legacy function)
export const uploadResumeForAccountCreation = async (userId, file, progressCallback = null) => {
  try {
    if (!userId || !file) {
      return { success: false, error: "Missing required information" };
    }
    
    // Validate file type
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      return { success: false, error: 'Please upload a valid resume file (PDF or Word document)' };
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      return { success: false, error: 'File size must be less than 5MB' };
    }
    
    // Create filename in the format expected by processor.js
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const timestamp = Date.now();
    const filename = `user_resumes/${userId}_${timestamp}.${fileExtension}`;
    
    console.log(`[Account Creation] Uploading resume as ${filename}`);
    
    // Create a reference to Firebase Storage
    const resumeRef = ref(storage, filename);
    
    // Use uploadBytesResumable for progress tracking
    const uploadTask = uploadBytesResumable(resumeRef, file);
    
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Calculate and report progress
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`[Account Creation] Resume upload progress: ${progress.toFixed(1)}%`);
          
          if (progressCallback) {
            progressCallback(Math.round(progress));
          }
        },
        (error) => {
          console.error('[Account Creation] Resume upload failed:', error);
          
          let errorMessage = "Failed to upload resume.";
          switch (error.code) {
            case 'storage/unauthorized':
              errorMessage = "You don't have permission to upload files.";
              break;
            case 'storage/canceled':
              errorMessage = "Upload was canceled.";
              break;
            case 'storage/quota-exceeded':
              errorMessage = "Storage quota exceeded.";
              break;
            case 'storage/invalid-argument':
              errorMessage = "Invalid file format.";
              break;
            case 'storage/unknown':
            default:
              errorMessage = `Upload error: ${error.message}`;
              break;
          }
          
          reject({ success: false, error: errorMessage });
        },
        async () => {
          try {
            // Upload completed successfully
            console.log('[Account Creation] Resume upload completed');
            
            // Get download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log('[Account Creation] Resume download URL:', downloadURL);
            
            resolve({
              success: true,
              resumeUrl: downloadURL,
              resumeName: file.name,
              storagePath: filename,
              timestamp: timestamp
            });
          } catch (urlError) {
            console.error("[Account Creation] Error getting resume download URL:", urlError);
            reject({ 
              success: false, 
              error: "Resume was uploaded but couldn't retrieve download URL"
            });
          }
        }
      );
    });
  } catch (error) {
    console.error("[Account Creation] Error setting up resume upload:", error);
    return { success: false, error: error.message };
  }
};